import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, TrendingUp, X, Zap } from 'lucide-react';
import { trackCTAClick } from '../lib/analytics';

/**
 * Detects whether a backend error message describes a quota / usage-cap
 * failure. When it does, `<QuotaErrorBanner/>` renders an upgrade CTA
 * pointing straight at the subscription plans; otherwise it falls back
 * to a plain red error banner.
 *
 * Kept intentionally free of business logic — a single source of truth
 * for "this looks like a paywall message" across every page that shows
 * server errors.
 */
const QUOTA_PATTERNS = [
  /quota/i,
  /monthly limit/i,
  /limit reached/i,
  /exceeded/i,
  /free.*plan.*limit/i,
  /upgrade.*plan/i,
];

export const looksLikeQuotaError = (msg) => {
  if (!msg || typeof msg !== 'string') return false;
  return QUOTA_PATTERNS.some((re) => re.test(msg));
};

/**
 * Props:
 *   error    — string. The error message from setError() / API detail.
 *   onClose  — optional. () => void. Renders close (X) button when set.
 *   source   — string. Analytics location tag for the CTA click event.
 *              e.g. "workflow-generation", "stl-repair", "image-analysis".
 */
const QuotaErrorBanner = ({ error, onClose, source = 'unknown' }) => {
  const navigate = useNavigate();
  if (!error) return null;

  const isQuota = looksLikeQuotaError(error);

  if (!isQuota) {
    // Non-quota errors keep the existing subtle red banner.
    return (
      <div
        className="p-4 bg-error/10 border border-error/20 rounded-xl flex items-start gap-3"
        data-testid="error-banner"
      >
        <AlertTriangle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-error text-sm">{error}</p>
        </div>
        {onClose && (
          <button onClick={onClose} aria-label="Dismiss error">
            <X className="w-4 h-4 text-error/60 hover:text-error" />
          </button>
        )}
      </div>
    );
  }

  // Quota error — much more prominent CTA with upgrade path.
  const goUpgrade = () => {
    trackCTAClick('Upgrade to Pro', `quota-error-${source}`);
    navigate('/dashboard/settings?tab=subscription');
  };

  return (
    <div
      className="rounded-xl border-2 border-primary/40 bg-gradient-to-br from-primary/15 via-bg-dark/40 to-accent/10 p-4 sm:p-5"
      data-testid="quota-error-banner"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-primary/20 border border-primary/40 flex items-center justify-center flex-shrink-0">
          <TrendingUp className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm sm:text-base font-bold text-text-primary mb-1">
            {error}
          </p>
          <p className="text-xs sm:text-sm text-text-secondary leading-relaxed">
            Upgrade to <span className="text-primary font-semibold">Pro</span> or{' '}
            <span className="text-primary font-semibold">Studio</span> for priority
            processing, higher monthly limits, and unlimited access to advanced
            features.
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="flex-shrink-0 -mr-1 -mt-1"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-text-muted hover:text-text-primary" />
          </button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={goUpgrade}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/80 text-white rounded-lg text-sm font-bold shadow-md shadow-primary/25 hover:scale-[1.01] transition"
          data-testid="quota-error-upgrade-btn"
        >
          <Zap className="w-4 h-4" />
          Upgrade now — get more generations
        </button>
      </div>
      <p className="text-[10px] text-text-muted text-center mt-2.5">
        Pro from $14/mo · Studio from $29/mo · Cancel anytime
      </p>
    </div>
  );
};

export default QuotaErrorBanner;
